import { IconDot } from "@/assets/icons/geist/IconDot";
import { IconGridEmpty } from "@/assets/icons/geist/IconGridEmpty";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { Delayed } from "@/components/podkit/loading/Delayed";
import { Tabs, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { Heading1, Heading2 } from "@/components/podkit/typography/Headings";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { CreateProjectModal } from "@/components/projects/CreateProjectModal";
import { ProjectsFilterType } from "@/components/projects/ProjectConstants";
import { ProjectSearchInput } from "@/components/projects/ProjectSearchInput";
import { ProjectsList } from "@/components/projects/ProjectsList";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useEnvironmentClassEntries } from "@/hooks/use-environment-class-entries";
import { useMayCreateProject } from "@/hooks/use-may-create-project";
import { useMembership } from "@/hooks/use-membership";
import { usePerformance } from "@/hooks/use-performance";
import { useListProjects, useListProjectStates } from "@/queries/project-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useState, type FC } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export const ProjectsPage: FC = () => {
    useDocumentTitle("Projects");
    const { markDataLoadStart, markDataLoadComplete } = usePerformance({
        component: "ProjectsPage",
    });
    const dataLoadStart = markDataLoadStart();

    const location = useLocation();
    const selectProjectId = (location.search && new URLSearchParams(location.search).get("project")) ?? undefined;
    const selectEnvironmentClass =
        (location.search && new URLSearchParams(location.search).get("environmentClass")) ?? undefined;
    const selectRunnerId = (location.search && new URLSearchParams(location.search).get("runnerId")) ?? undefined;
    const isFiltering = !!selectProjectId || !!selectEnvironmentClass || !!selectRunnerId;

    const { data: projectsData, isPending: isPendingProjects } = useListProjects();
    const { membership } = useMembership();

    const { mayCreateProject } = useMayCreateProject();

    const [projectsFilterType, setProjectsFilterType] = useState(ProjectsFilterType.Active);
    const [searchValue, setSearchValue] = useState<string>();
    const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

    const handleCreateProject = useCallback(() => {
        setShowCreateProjectModal(true);
    }, [setShowCreateProjectModal]);

    const { data: projectsStates, isLoading: isLoadingProjectsStates } = useListProjectStates(
        projectsData?.projects.map((p) => p.id),
    );

    useEffect(() => {
        if (!isPendingProjects && !isLoadingProjectsStates) {
            markDataLoadComplete(dataLoadStart);
        }
    }, [markDataLoadComplete, dataLoadStart, isPendingProjects, isLoadingProjectsStates]);

    // Migrate from our old deep links to project details pages.
    const navigate = useNavigate();
    useEffect(() => {
        if (selectProjectId) {
            navigate(`/projects/${selectProjectId}`, { replace: true });
        }
    }, [selectProjectId, navigate]);

    const isEmpty = !isPendingProjects && projectsData?.projects.length === 0;

    const notSharedNotification =
        !isLoadingProjectsStates &&
        projectsData?.projects.some(
            (p) =>
                p.metadata?.creator?.id === membership?.userId &&
                projectsStates?.some((ps) => ps.projectId === p.id && typeof ps.shared !== "undefined" && !ps.shared),
        );

    return (
        <div data-testid="projects-page" className="h-full">
            <div className="flex size-full flex-col gap-4">
                <div className="flex items-center">
                    <Heading1 className="flex grow flex-row align-middle text-content-primary">Projects</Heading1>
                    {mayCreateProject && !isEmpty && (
                        <Delayed>
                            <Button
                                LeadingIcon={IconPlus}
                                variant="primary"
                                onClick={handleCreateProject}
                                data-tracking-id="new-project-projects-page"
                            >
                                New Project
                            </Button>
                        </Delayed>
                    )}
                </div>
                <>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <ProjectsFilterTabList
                            filterType={projectsFilterType}
                            onFilterTypeChange={(v) => setProjectsFilterType(v)}
                            disabled={isFiltering}
                            notSharedNotification={notSharedNotification}
                        />
                        <div className="relative flex w-full sm:flex-1">
                            <ProjectSearchInput
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                                disabled={isFiltering}
                            />
                        </div>
                    </div>
                    {isEmpty && !isPendingProjects && (
                        <div className="mt-[100px] flex size-full flex-col items-center justify-start gap-2">
                            <NoProjects handleCreateProject={handleCreateProject} />
                        </div>
                    )}
                    {!isEmpty && (
                        <ProjectsList
                            showRecentlyUsedOnly={true}
                            canEdit={true}
                            filter={projectsFilterType}
                            search={searchValue}
                            selectEnvironmentClass={selectEnvironmentClass}
                            selectRunnerId={selectRunnerId}
                        />
                    )}
                </>
                {showCreateProjectModal && <CreateProjectModal onClose={() => setShowCreateProjectModal(false)} />}
            </div>
        </div>
    );
};

const NoProjects: FC<{
    handleCreateProject: () => void;
}> = ({ handleCreateProject }) => {
    const { membership } = useMembership();
    const isAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const { mayCreateProject } = useMayCreateProject();

    const { environmentClassEntries, isLoading: isLoadingEnvClasses } = useEnvironmentClassEntries();
    const hasNoEnvClasses = !isLoadingEnvClasses && environmentClassEntries.length === 0;

    return (
        <>
            <IconGridEmpty className="mb-1 size-[90px] text-content-primary" />
            <Heading2>No projects</Heading2>
            <div className="max-w-[480px] pb-2">
                <Text className="text-center text-base">
                    Projects bundle your repo, secrets, and other configuration into a shareable template—prebuilt in
                    the background for faster startup times.{" "}
                    <ExternalLink href="https://ona.com/docs/ona/projects/overview" iconSize="sm">
                        Learn more about projects in Ona.
                    </ExternalLink>
                </Text>
                {!isAdmin && (
                    <Text className="pt-2 text-center text-xs">
                        Only admins can create projects for your organization.
                    </Text>
                )}
            </div>
            {mayCreateProject && !isLoadingEnvClasses && (
                <>
                    {hasNoEnvClasses ? (
                        <Text className="pb-2 text-center text-base font-semibold">
                            Before you can create a project, you need to{" "}
                            <Link to="/settings/runners" className="underline">
                                set up a runner
                            </Link>
                            .
                        </Text>
                    ) : (
                        <Button
                            data-testid="create-project-button"
                            onClick={handleCreateProject}
                            data-tracking-id="create-project-no-projects"
                        >
                            Create Project
                        </Button>
                    )}
                </>
            )}
        </>
    );
};

const ProjectsFilterTabList: FC<{
    filterType: ProjectsFilterType;
    disabled?: boolean;
    onFilterTypeChange: (filterType: ProjectsFilterType) => void;
    notSharedNotification?: boolean;
}> = ({ filterType, disabled, onFilterTypeChange, notSharedNotification }) => {
    return (
        <Tabs
            value={filterType}
            onValueChange={(value) => onFilterTypeChange(value as ProjectsFilterType)}
            className="contents"
        >
            <TabsList className={cn("w-full flex-row sm:w-fit", disabled && "pointer-events-none opacity-50")}>
                <TabsTrigger
                    value={ProjectsFilterType.Active}
                    className="justify-center text-center"
                    data-tracking-id="filter-shared-projects-projects-page"
                >
                    Shared Projects
                </TabsTrigger>
                <TabsTrigger
                    value={ProjectsFilterType.NotShared}
                    className="relative justify-center text-center"
                    data-tracking-id="filter-not-shared-projects-projects-page"
                >
                    Not Shared
                    {notSharedNotification && (
                        <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center">
                            <IconDot size="sm" className="text-content-green" />
                        </span>
                    )}
                </TabsTrigger>
                <TabsTrigger
                    value={ProjectsFilterType.All}
                    className="justify-center text-center"
                    data-tracking-id="filter-all-projects-projects-page"
                >
                    All Projects
                </TabsTrigger>
            </TabsList>
        </Tabs>
    );
};
